import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { API, formatApiErrorDetail } from "@/lib/api";
import { Layout } from "@/components/Layout";
import { OrdineForm } from "@/components/OrdineForm";
import { SizeChips } from "@/components/SizeChips";
import { toast } from "sonner";
import { ArrowLeft, Loader2, Plus, Trash2, Download, Lock, Unlock, Rocket, FileSpreadsheet, Upload, Search, X } from "lucide-react";

const eur = (n) => `${Number(n || 0).toFixed(2).replace(".", ",")} €`;
const STATO_PROD = { raccolta_ordini: "Raccolta ordini", ordini_chiusi: "Ordini chiusi", in_preparazione: "In preparazione", in_produzione: "In produzione", pronto: "Pronto", consegnato: "Consegnato" };
const STATO_PAG = { da_pagare: { l: "Da pagare", c: "bg-red-400/15 text-red-300 border-red-400/30" }, acconto: { l: "Acconto", c: "bg-amber-400/15 text-amber-300 border-amber-400/30" }, saldato: { l: "Saldato", c: "bg-emerald-400/15 text-emerald-300 border-emerald-400/30" } };
const TABS = [["pagamenti", "Pagamenti"], ["versamenti", "Versamenti"], ["produzione", "Produzione"], ["file", "File"], ["impostazioni", "Impostazioni"]];

const dlUrl = (path) => `${API}${path}`;

export default function RaccoltaDettaglio() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [r, setR] = useState(null);
  const [tab, setTab] = useState("produzione");
  const [loading, setLoading] = useState(true);

  const loadR = useCallback(() => {
    api.get(`/raccolta/raccolte/${id}`).then(({ data }) => setR(data)).catch(() => toast.error("Raccolta non trovata")).finally(() => setLoading(false));
  }, [id]);
  useEffect(loadR, [loadR]);

  const downloadFile = (path) => {
    // Apre l'export in una nuova scheda: l'auth passa via cookie di sessione,
    // così l'app non viene ricaricata (evita il ritorno forzato al login su mobile).
    window.open(`${API}${path}`, "_blank", "noopener");
  };

  if (loading || !r) return <Layout><div className="flex items-center gap-2 text-slate-400"><Loader2 className="w-5 h-5 animate-spin" /> Caricamento...</div></Layout>;

  const soCls = r.stato_ordini === "aperta" ? "bg-emerald-400/15 text-emerald-300 border-emerald-400/30" : r.stato_ordini === "chiusa" ? "bg-red-400/15 text-red-300 border-red-400/30" : "bg-slate-500/15 text-slate-300 border-slate-500/30";

  return (
    <Layout>
      <button onClick={() => navigate("/raccolta-ordini")} className="inline-flex items-center gap-1.5 text-sm text-slate-400 hover:text-slate-200 mb-4"><ArrowLeft className="w-4 h-4" /> Tutte le raccolte</button>

      <div className="glass rounded-xl p-4 mb-4">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h1 className="text-2xl font-bold text-slate-100">{r.nome_gruppo}</h1>
            <p className="text-sm text-slate-500">{r.tipologia_gruppo} · {r.festa} · {r.edizione} · Responsabile: {r.responsabile_nome}</p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <span className={`text-xs px-2.5 py-1 rounded-full border ${soCls}`}>Ordini: {r.stato_ordini}</span>
            <span className="text-xs px-2.5 py-1 rounded-full border bg-sky-400/15 text-sky-300 border-sky-400/30">Produzione: {STATO_PROD[r.stato_produzione]}</span>
            {r.data_stimata_consegna && <span className="text-xs px-2.5 py-1 rounded-full border bg-violet-400/15 text-violet-300 border-violet-400/30">Consegna stimata: {r.data_stimata_consegna}</span>}
          </div>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-6 gap-3 mt-4 text-center">
          {[["Ordini", r.num_ordini, "text-slate-200"], ["Valore", eur(r.valore_ordini), "text-emerald-300"], ["Incassato", eur(r.incassato), "text-cyan-300"], ["Da incassare", eur(r.da_incassare), "text-amber-300"], ["Consegnato", eur(r.consegnato), "text-sky-300"], ["Saldo resp.", eur(r.saldo_da_consegnare), "text-violet-300"]].map(([l, v, c]) => (
            <div key={l}><div className={`text-lg font-bold ${c}`}>{v}</div><div className="text-[10px] uppercase text-slate-500">{l}</div></div>
          ))}
        </div>
      </div>

      <div className="flex flex-wrap gap-1.5 mb-4">
        {TABS.map(([k, l]) => (
          <button key={k} data-testid={`tab-${k}`} onClick={() => setTab(k)} className={`px-3.5 py-2 rounded-lg text-sm font-medium whitespace-nowrap border ${tab === k ? "bg-cyan-400/15 text-cyan-300 border-cyan-400/40" : "text-slate-400 border-transparent hover:bg-slate-800"}`}>{l}</button>
        ))}
      </div>

      {tab === "pagamenti" && <TabPagamenti raccolta={r} onChanged={loadR} />}
      {tab === "versamenti" && <TabVersamenti raccolta={r} onChanged={loadR} />}
      {tab === "produzione" && <TabProduzione raccolta={r} onChanged={loadR} downloadFile={downloadFile} />}
      {tab === "file" && <TabFile raccolta={r} downloadFile={downloadFile} />}
      {tab === "impostazioni" && <TabImpostazioni raccolta={r} onChanged={loadR} />}
    </Layout>
  );
}

function useProdotti(id) {
  const [prodotti, setProdotti] = useState([]);
  const reload = useCallback(() => { api.get(`/raccolta/raccolte/${id}/prodotti`).then(({ data }) => setProdotti(data)).catch(() => {}); }, [id]);
  useEffect(() => { reload(); }, [reload]);
  return [prodotti, reload];
}

// eslint-disable-next-line no-unused-vars
function TabOrdini({ raccolta, onChanged }) {
  const [prodotti] = useProdotti(raccolta.id);
  const [data, setData] = useState({ items: [], total: 0, page: 1, page_size: 50 });
  const [search, setSearch] = useState("");
  const [stato, setStato] = useState("");
  const [form, setForm] = useState(false);
  const [edit, setEdit] = useState(null);

  const load = useCallback(() => {
    const p = new URLSearchParams(); if (search) p.append("search", search); if (stato) p.append("stato", stato);
    api.get(`/raccolta/raccolte/${raccolta.id}/ordini?${p.toString()}`).then(({ data }) => setData(data)).catch(() => {});
  }, [raccolta.id, search, stato]);
  useEffect(load, [load]);

  const create = async (payload) => {
    try { await api.post(`/raccolta/raccolte/${raccolta.id}/ordini`, payload); toast.success("Ordine creato"); setForm(false); load(); onChanged(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); throw e; }
  };
  const update = async (payload) => {
    try { await api.patch(`/raccolta/raccolte/${raccolta.id}/ordini/${edit.id}`, payload); toast.success("Ordine aggiornato"); setEdit(null); load(); onChanged(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); throw e; }
  };
  const del = async (o) => {
    if (!window.confirm(`Eliminare l'ordine di ${o.nome} ${o.cognome}?`)) return;
    try { await api.delete(`/raccolta/raccolte/${raccolta.id}/ordini/${o.id}`); toast.success("Ordine eliminato"); load(); onChanged(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  if (form || edit) return (
    <div className="glass rounded-xl p-4">
      <h3 className="text-lg font-bold text-slate-100 mb-3">{edit ? "Modifica ordine" : "Nuovo ordine"}</h3>
      {prodotti.filter((p) => p.attivo).length === 0 ? <p className="text-amber-300 text-sm">Aggiungi prima dei prodotti nella tab Impostazioni.</p> :
        <OrdineForm prodotti={prodotti.filter((p) => p.attivo)} initial={edit} onSubmit={edit ? update : create} onCancel={() => { setForm(false); setEdit(null); }} />}
    </div>
  );

  return (
    <div className="space-y-3">
      <div className="flex gap-2 flex-wrap items-center">
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
          <input data-testid="ordini-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Cerca nome/telefono..." className="pl-9 pr-3 py-2 bg-slate-900/60 border border-slate-700 rounded-lg text-sm text-slate-100" />
        </div>
        <select data-testid="ordini-filtro-stato" value={stato} onChange={(e) => setStato(e.target.value)} className="bg-slate-900/60 border border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-200">
          <option value="">Tutti i pagamenti</option>
          <option value="da_pagare">Da pagare</option><option value="acconto">Acconto</option><option value="saldato">Saldato</option>
        </select>
        <button data-testid="ordini-nuovo" onClick={() => setForm(true)} className="ml-auto inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-cyan-400 hover:bg-cyan-300 text-slate-900 font-semibold text-sm"><Plus className="w-4 h-4" /> Nuovo ordine</button>
      </div>
      {data.items.length === 0 ? <p data-testid="ordini-empty" className="text-slate-500 text-sm">Nessun ordine.</p> : (
        <div className="space-y-2" data-testid="ordini-list">
          {data.items.map((o) => {
            const sp = STATO_PAG[o.stato_pagamento] || STATO_PAG.da_pagare;
            return (
              <div key={o.id} data-testid={`ordine-${o.id}`} className="glass rounded-xl p-3">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <div className="font-semibold text-slate-100">{o.nome} {o.cognome} {o.ordine_eccezionale && <span className="ml-1 text-[10px] px-1.5 py-0.5 rounded bg-orange-400/15 text-orange-300 border border-orange-400/30">AGGIUNTA ECCEZIONALE</span>}</div>
                    <div className="text-xs text-slate-500">{o.cellulare}</div>
                  </div>
                  <span className={`text-xs px-2 py-0.5 rounded-full border ${sp.c}`}>{sp.l}</span>
                </div>
                <div className="text-sm text-slate-400 mt-2 space-y-0.5">
                  {o.righe.map((rg) => <div key={rg.id}>{rg.quantita}× {rg.nome_prodotto_snapshot}{rg.taglia ? ` · ${rg.taglia}` : ""}{rg.nome_retro ? ` · retro ${rg.nome_retro}` : ""} — {eur(rg.subtotale)}</div>)}
                </div>
                <div className="flex items-center justify-between mt-2 border-t border-slate-800 pt-2">
                  <span className="text-sm text-slate-400">Tot {eur(o.totale)} · Inc {eur(o.importo_incassato)} · Res {eur(o.residuo)}</span>
                  <div className="flex gap-2">
                    <button data-testid={`ordine-edit-${o.id}`} onClick={() => setEdit(o)} className="text-xs px-2 py-1 rounded-lg bg-slate-700/40 text-slate-300 border border-slate-700">Modifica</button>
                    <button data-testid={`ordine-del-${o.id}`} onClick={() => del(o)} className="text-xs px-2 py-1 rounded-lg bg-red-500/10 text-red-300 border border-red-500/30"><Trash2 className="w-3.5 h-3.5" /></button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function TabPagamenti({ raccolta, onChanged }) {
  const [items, setItems] = useState([]);
  const load = useCallback(() => { api.get(`/raccolta/raccolte/${raccolta.id}/ordini?page_size=200`).then(({ data }) => setItems(data.items || [])).catch(() => {}); }, [raccolta.id]);
  useEffect(load, [load]);
  const [vals, setVals] = useState({});
  const save = async (o) => {
    const v = vals[o.id] ?? o.importo_incassato;
    try { await api.patch(`/raccolta/raccolte/${raccolta.id}/ordini/${o.id}/pagamento`, { importo_incassato: parseFloat(v) || 0 }); toast.success("Pagamento aggiornato"); load(); onChanged(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  return (
    <div className="space-y-2" data-testid="pagamenti-list">
      {items.length === 0 && <p className="text-slate-500 text-sm">Nessun ordine.</p>}
      {items.map((o) => {
        const sp = STATO_PAG[o.stato_pagamento] || STATO_PAG.da_pagare;
        return (
          <div key={o.id} className="glass rounded-xl p-3 flex items-center justify-between gap-3 flex-wrap">
            <div><div className="font-semibold text-slate-100">{o.nome} {o.cognome}</div><div className="text-xs text-slate-500">Totale {eur(o.totale)} · Residuo {eur(o.residuo)}</div></div>
            <div className="flex items-center gap-2">
              <span className={`text-xs px-2 py-0.5 rounded-full border ${sp.c}`}>{sp.l}</span>
              <input data-testid={`pag-input-${o.id}`} type="number" step="0.01" defaultValue={o.importo_incassato} onChange={(e) => setVals((s) => ({ ...s, [o.id]: e.target.value }))} className="w-28 bg-slate-900/60 border border-slate-700 rounded-lg px-3 py-1.5 text-slate-100 text-sm" />
              <button data-testid={`pag-save-${o.id}`} onClick={() => save(o)} className="text-xs px-2.5 py-1.5 rounded-lg bg-cyan-400 hover:bg-cyan-300 text-slate-900 font-semibold">Salva</button>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function TabVersamenti({ raccolta, onChanged }) {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ importo: "", metodo: "Contanti", nota: "" });
  const load = useCallback(() => { api.get(`/raccolta/raccolte/${raccolta.id}/versamenti`).then(({ data }) => setItems(Array.isArray(data) ? data : [])).catch(() => {}); }, [raccolta.id]);
  useEffect(load, [load]);
  const add = async () => {
    if (!(parseFloat(form.importo) > 0)) return toast.error("Importo non valido");
    try { await api.post(`/raccolta/raccolte/${raccolta.id}/versamenti`, { importo: parseFloat(form.importo), metodo: form.metodo, nota: form.nota }); toast.success("Versamento registrato"); setForm({ importo: "", metodo: "Contanti", nota: "" }); load(); onChanged(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const del = async (v) => { if (!window.confirm("Eliminare il versamento?")) return; try { await api.delete(`/raccolta/raccolte/versamenti/${v.id}`); load(); onChanged(); } catch { toast.error("Errore"); } };
  return (
    <div className="space-y-3">
      <div className="glass rounded-xl p-3 flex gap-2 flex-wrap items-end">
        <div><label className="text-[10px] uppercase text-slate-500">Importo</label><input data-testid="vers-importo" type="number" step="0.01" value={form.importo} onChange={(e) => setForm((f) => ({ ...f, importo: e.target.value }))} className="w-28 bg-slate-900/60 border border-slate-700 rounded-lg px-3 py-2 text-slate-100" /></div>
        <div><label className="text-[10px] uppercase text-slate-500">Metodo</label><select data-testid="vers-metodo" value={form.metodo} onChange={(e) => setForm((f) => ({ ...f, metodo: e.target.value }))} className="bg-slate-900/60 border border-slate-700 rounded-lg px-3 py-2 text-slate-100"><option>Contanti</option><option>Bonifico</option><option>Altro</option></select></div>
        <input data-testid="vers-nota" value={form.nota} onChange={(e) => setForm((f) => ({ ...f, nota: e.target.value }))} placeholder="Nota" className="flex-1 min-w-[120px] bg-slate-900/60 border border-slate-700 rounded-lg px-3 py-2 text-slate-100" />
        <button data-testid="vers-add" onClick={add} className="px-3 py-2 rounded-lg bg-cyan-400 hover:bg-cyan-300 text-slate-900 font-semibold text-sm inline-flex items-center gap-1"><Plus className="w-4 h-4" /> Registra</button>
      </div>
      <div className="space-y-2" data-testid="versamenti-list">
        {items.length === 0 && <p className="text-slate-500 text-sm">Nessun versamento.</p>}
        {items.map((v) => (
          <div key={v.id} className="glass rounded-xl p-3 flex items-center justify-between">
            <div><span className="font-mono-tech text-emerald-300 font-bold">{eur(v.importo)}</span> <span className="text-slate-400 text-sm">· {v.metodo} · {(v.data_versamento || "").slice(0, 10)}{v.nota ? ` · ${v.nota}` : ""}</span></div>
            <button onClick={() => del(v)} className="text-xs px-2 py-1 rounded-lg bg-red-500/10 text-red-300 border border-red-500/30"><Trash2 className="w-3.5 h-3.5" /></button>
          </div>
        ))}
      </div>
    </div>
  );
}

function TabProduzione({ raccolta, onChanged, downloadFile }) {
  const [prodotti, reloadP] = useProdotti(raccolta.id);
  const [riep, setRiep] = useState([]);
  const [statoProd, setStatoProd] = useState(raccolta.stato_produzione);
  const [avvio, setAvvio] = useState("");
  useEffect(() => { api.get(`/raccolta/raccolte/${raccolta.id}/riepilogo-taglie`).then(({ data }) => setRiep(data)).catch(() => {}); }, [raccolta.id]);

  const saveStato = async () => {
    try { await api.patch(`/raccolta/raccolte/${raccolta.id}/stato-produzione`, { stato_produzione: statoProd, data_avvio_produzione: avvio ? new Date(avvio).toISOString() : null }); toast.success("Stato produzione aggiornato"); onChanged(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const avviaProd = async () => {
    try { const { data } = await api.post(`/raccolta/raccolte/${raccolta.id}/avvia-produzione`, { data_avvio_produzione: avvio ? new Date(avvio).toISOString() : null }); toast.success(`Produzione avviata. Consegna stimata: ${data.data_stimata_consegna}`); onChanged(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const saveCodici = async (p, patch) => {
    try { await api.patch(`/raccolta/raccolte/prodotti/${p.id}`, patch); toast.success("Salvato"); reloadP(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-4">
      <div className="glass rounded-xl p-4 space-y-3">
        <h3 className="font-bold text-slate-100">Stato produzione & consegna</h3>
        <div className="text-xs text-slate-400 bg-slate-900/40 rounded-lg p-3">
          Le maglie sublimatiche prevedono una tempistica di produzione e consegna di <b>15 giorni lavorativi</b>, esclusi sabato e domenica. Gli altri articoli della raccolta vengono consegnati insieme alle maglie.
        </div>
        <div className="flex gap-2 flex-wrap items-end">
          <div><label className="text-[10px] uppercase text-slate-500">Stato</label><select data-testid="prod-stato-select" value={statoProd} onChange={(e) => setStatoProd(e.target.value)} className="bg-slate-900/60 border border-slate-700 rounded-lg px-3 py-2 text-slate-100">{Object.entries(STATO_PROD).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select></div>
          <div><label className="text-[10px] uppercase text-slate-500">Data avvio produzione</label><input data-testid="prod-avvio-date" type="datetime-local" value={avvio} onChange={(e) => setAvvio(e.target.value)} className="bg-slate-900/60 border border-slate-700 rounded-lg px-3 py-2 text-slate-100" /></div>
          <button data-testid="prod-stato-save" onClick={saveStato} className="px-3 py-2 rounded-lg bg-slate-700/40 border border-slate-700 text-slate-200 text-sm">Salva stato</button>
          <button data-testid="prod-avvia" onClick={avviaProd} className="px-3 py-2 rounded-lg bg-emerald-500/20 text-emerald-200 border border-emerald-500/40 text-sm inline-flex items-center gap-1"><Rocket className="w-4 h-4" /> Avvia produzione</button>
        </div>
        {raccolta.data_avvio_produzione && <div className="text-sm text-slate-400">Avvio: <b className="text-slate-200">{raccolta.data_avvio_produzione}</b> · Consegna stimata: <b className="text-violet-300">{raccolta.data_stimata_consegna}</b></div>}
      </div>

      <div className="glass rounded-xl p-4 space-y-2">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <h3 className="font-bold text-slate-100">Export</h3>
          <div className="flex gap-2">
            <button data-testid="export-fabbrica" onClick={() => downloadFile(`/raccolta/raccolte/${raccolta.id}/export/fabbrica`, `Jersey_${raccolta.nome_gruppo}.xlsx`)} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-orange-400/15 text-orange-200 border border-orange-400/30 text-sm"><Factory className="w-4 h-4" /> Excel Fabbrica Jersey</button>
            <button data-testid="export-interna" onClick={() => downloadFile(`/raccolta/raccolte/${raccolta.id}/export/interna`, `ProduzioneInterna_${raccolta.nome_gruppo}.xlsx`)} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-sky-400/15 text-sky-200 border border-sky-400/30 text-sm"><FileSpreadsheet className="w-4 h-4" /> Excel Produzione Interna</button>
          </div>
        </div>
      </div>

      <div className="glass rounded-xl p-4">
        <h3 className="font-bold text-slate-100 mb-3">Riepilogo produzione</h3>
        {riep.length === 0 ? <p className="text-slate-500 text-sm">Nessun capo ordinato.</p> : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="riepilogo-produzione">
            {riep.map((p) => (
              <div key={p.prodotto_id} className="bg-slate-900/40 rounded-lg p-3">
                <div className="flex items-center justify-between"><span className="font-semibold text-slate-200">{p.nome}</span><span className="text-xs px-2 py-0.5 rounded-full border bg-slate-700/40 text-slate-300 border-slate-700">Tot {p.totale}</span></div>
                <div className="text-sm text-slate-400 mt-1 flex flex-wrap gap-x-3">{p.per_taglia.map((t) => <span key={t.taglia}>{t.taglia}: <b className="text-slate-200">{t.quantita}</b></span>)}</div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="glass rounded-xl p-4">
        <h3 className="font-bold text-slate-100 mb-3">Dati produzione articoli</h3>
        <div className="space-y-2">
          {prodotti.map((p) => (
            <ProdCodici key={p.id} p={p} onSave={saveCodici} />
          ))}
        </div>
      </div>
    </div>
  );
}

function Factory(props) { return <Rocket {...props} />; }

function ProdCodici({ p, onSave }) {
  const [cod, setCod] = useState(p.cod_articolo || "");
  const [graf, setGraf] = useState(p.cod_grafica || "");
  const [note, setNote] = useState(p.note_produzione || "");
  return (
    <div className="bg-slate-900/40 rounded-lg p-3">
      <div className="flex items-center justify-between mb-2"><span className="font-semibold text-slate-200 text-sm">{p.nome_snapshot}</span><span className={`text-[10px] px-2 py-0.5 rounded-full border ${p.produzione === "esterna" ? "bg-orange-400/15 text-orange-300 border-orange-400/30" : "bg-sky-400/15 text-sky-300 border-sky-400/30"}`}>{p.produzione}</span></div>
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-2">
        <input data-testid={`cod-articolo-${p.id}`} value={cod} onChange={(e) => setCod(e.target.value)} placeholder="Cod. Articolo" className="bg-slate-900/60 border border-slate-700 rounded-lg px-3 py-1.5 text-slate-100 text-sm" />
        <input data-testid={`cod-grafica-${p.id}`} value={graf} onChange={(e) => setGraf(e.target.value)} placeholder="Cod. Grafica" className="bg-slate-900/60 border border-slate-700 rounded-lg px-3 py-1.5 text-slate-100 text-sm" />
        <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Note produzione" className="bg-slate-900/60 border border-slate-700 rounded-lg px-3 py-1.5 text-slate-100 text-sm" />
        <button data-testid={`cod-save-${p.id}`} onClick={() => onSave(p, { cod_articolo: cod, cod_grafica: graf, note_produzione: note })} className="px-3 py-1.5 rounded-lg bg-cyan-400 hover:bg-cyan-300 text-slate-900 font-semibold text-sm">Salva</button>
      </div>
    </div>
  );
}

function TabFile({ raccolta, downloadFile }) {
  const [prodotti, reloadP] = useProdotti(raccolta.id);
  const upload = async (p, tipo, file) => {
    if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try { await api.post(`/raccolta/raccolte/prodotti/${p.id}/file?tipo=${tipo}`, fd, { headers: { "Content-Type": "multipart/form-data" } }); toast.success("File caricato"); reloadP(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  return (
    <div className="glass rounded-xl p-4 space-y-2">
      <h3 className="font-bold text-slate-100 mb-2">File produzione (solo staff)</h3>
      {prodotti.map((p) => (
        <div key={p.id} className="bg-slate-900/40 rounded-lg p-3">
          <div className="font-semibold text-slate-200 text-sm mb-2">{p.nome_snapshot}</div>
          <div className="flex gap-3 flex-wrap">
            {[["grafica", "file_grafica_id"], ["stampa", "file_stampa_id"]].map(([tipo, field]) => (
              <div key={tipo} className="flex items-center gap-2">
                <label data-testid={`file-upload-${tipo}-${p.id}`} className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-lg bg-slate-700/40 text-slate-200 border border-slate-700 cursor-pointer hover:bg-slate-700">
                  <Upload className="w-3.5 h-3.5" /> {tipo === "grafica" ? "Grafica" : "File stampa"}
                  <input type="file" className="hidden" onChange={(e) => upload(p, tipo, e.target.files[0])} />
                </label>
                {p[field] && <button data-testid={`file-dl-${tipo}-${p.id}`} onClick={() => downloadFile(`/raccolta/file/${p[field]}`, `${tipo}_${p.nome_snapshot}`)} className="text-xs px-2 py-1.5 rounded-lg bg-emerald-500/15 text-emerald-300 border border-emerald-500/30 inline-flex items-center gap-1"><Download className="w-3.5 h-3.5" /> Scarica</button>}
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function TabImpostazioni({ raccolta, onChanged }) {
  const [prodotti, reloadP] = useProdotti(raccolta.id);
  const [catalogo, setCatalogo] = useState([]);
  const [addId, setAddId] = useState("");
  const [addPrezzo, setAddPrezzo] = useState("");
  const [auth, setAuth] = useState({ quantita_slot: 1, motivo: "" });
  useEffect(() => { api.get("/raccolta/catalogo?solo_attivi=true").then(({ data }) => setCatalogo(data)).catch(() => {}); }, []);

  const addProdotto = async () => {
    if (!addId) return toast.error("Seleziona un articolo");
    try { await api.post(`/raccolta/raccolte/${raccolta.id}/prodotti`, { catalogo_articolo_id: addId, prezzo: parseFloat(addPrezzo) || 0 }); toast.success("Articolo aggiunto"); setAddId(""); setAddPrezzo(""); reloadP(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const patchProdotto = async (p, patch) => { try { await api.patch(`/raccolta/raccolte/prodotti/${p.id}`, patch); reloadP(); } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } };
  const delProdotto = async (p) => { if (!window.confirm("Rimuovere l'articolo dalla raccolta?")) return; try { await api.delete(`/raccolta/raccolte/prodotti/${p.id}`); reloadP(); } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } };

  const chiudi = async () => { try { await api.post(`/raccolta/raccolte/${raccolta.id}/chiudi`); toast.success("Raccolta chiusa"); onChanged(); } catch { toast.error("Errore"); } };
  const riapri = async () => { try { await api.post(`/raccolta/raccolte/${raccolta.id}/riapri`); toast.success("Raccolta riaperta"); onChanged(); } catch { toast.error("Errore"); } };
  const autorizza = async () => { try { await api.post(`/raccolta/raccolte/${raccolta.id}/autorizzazioni`, auth); toast.success(`Autorizzati ${auth.quantita_slot} ordini eccezionali`); setAuth({ quantita_slot: 1, motivo: "" }); onChanged(); } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } };

  return (
    <div className="space-y-4">
      <div className="glass rounded-xl p-4 space-y-3">
        <h3 className="font-bold text-slate-100">Apertura / Chiusura</h3>
        <p className="text-xs text-slate-400">Stato attuale: <b className="text-slate-200">{raccolta.stato_ordini}</b> · Slot eccezionali disponibili: <b className="text-orange-300">{raccolta.slot_disponibili}</b></p>
        <p className="text-xs text-amber-300/80">Dopo la scadenza non sarà più possibile aggiungere o modificare ordini salvo autorizzazione di Gigli Edition.</p>
        <div className="flex gap-2 flex-wrap">
          <button data-testid="raccolta-chiudi" onClick={chiudi} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-red-500/15 text-red-300 border border-red-500/30 text-sm"><Lock className="w-4 h-4" /> Chiudi ora</button>
          <button data-testid="raccolta-riapri" onClick={riapri} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-emerald-500/15 text-emerald-300 border border-emerald-500/30 text-sm"><Unlock className="w-4 h-4" /> Riapri</button>
        </div>
        <div className="flex gap-2 flex-wrap items-end border-t border-slate-800 pt-3">
          <div><label className="text-[10px] uppercase text-slate-500">Slot eccezionali</label><input data-testid="auth-slot" type="number" min="1" value={auth.quantita_slot} onChange={(e) => setAuth((a) => ({ ...a, quantita_slot: parseInt(e.target.value, 10) || 1 }))} className="w-24 bg-slate-900/60 border border-slate-700 rounded-lg px-3 py-2 text-slate-100" /></div>
          <input data-testid="auth-motivo" value={auth.motivo} onChange={(e) => setAuth((a) => ({ ...a, motivo: e.target.value }))} placeholder="Motivo (opzionale)" className="flex-1 min-w-[120px] bg-slate-900/60 border border-slate-700 rounded-lg px-3 py-2 text-slate-100" />
          <button data-testid="auth-add" onClick={autorizza} className="px-3 py-2 rounded-lg bg-orange-400/15 text-orange-200 border border-orange-400/30 text-sm">Autorizza aggiunta eccezionale</button>
        </div>
      </div>

      <div className="glass rounded-xl p-4 space-y-3">
        <h3 className="font-bold text-slate-100">Articoli della raccolta</h3>
        <div className="flex gap-2 flex-wrap items-end">
          <select data-testid="add-prodotto-select" value={addId} onChange={(e) => setAddId(e.target.value)} className="flex-1 min-w-[160px] bg-slate-900/60 border border-slate-700 rounded-lg px-3 py-2 text-slate-100">
            <option value="">+ Aggiungi articolo dal catalogo</option>
            {catalogo.filter((c) => !prodotti.some((p) => p.catalogo_articolo_id === c.id)).map((c) => <option key={c.id} value={c.id}>{c.nome}</option>)}
          </select>
          <input data-testid="add-prodotto-prezzo" type="number" step="0.01" value={addPrezzo} onChange={(e) => setAddPrezzo(e.target.value)} placeholder="Prezzo €" className="w-28 bg-slate-900/60 border border-slate-700 rounded-lg px-3 py-2 text-slate-100" />
          <button data-testid="add-prodotto-btn" onClick={addProdotto} className="px-3 py-2 rounded-lg bg-cyan-400 hover:bg-cyan-300 text-slate-900 font-semibold text-sm inline-flex items-center gap-1"><Plus className="w-4 h-4" /> Aggiungi</button>
        </div>
        <div className="space-y-2">
          {prodotti.map((p) => <ProdConfig key={p.id} p={p} onPatch={patchProdotto} onDelete={delProdotto} />)}
        </div>
      </div>
    </div>
  );
}

function ProdConfig({ p, onPatch, onDelete }) {
  const [prezzo, setPrezzo] = useState(p.prezzo);
  const [taglie, setTaglie] = useState(p.taglie_disponibili || []);
  return (
    <div className="bg-slate-900/40 rounded-lg p-3" data-testid={`prodotto-config-${p.id}`}>
      <div className="flex items-center justify-between mb-2">
        <span className="font-semibold text-slate-200">{p.nome_snapshot}</span>
        <div className="flex items-center gap-2">
          <button data-testid={`prod-toggle-${p.id}`} onClick={() => onPatch(p, { attivo: !p.attivo })} className={`text-xs px-2 py-0.5 rounded-full border ${p.attivo ? "bg-emerald-400/15 text-emerald-300 border-emerald-400/30" : "bg-slate-600/20 text-slate-400 border-slate-600/40"}`}>{p.attivo ? "Attivo" : "Disattivo"}</button>
          <button onClick={() => onDelete(p)} className="text-xs px-2 py-1 rounded-lg bg-red-500/10 text-red-300 border border-red-500/30"><Trash2 className="w-3.5 h-3.5" /></button>
        </div>
      </div>
      <div className="flex gap-2 items-end flex-wrap mb-2">
        <div><label className="text-[10px] uppercase text-slate-500">Prezzo €</label><input data-testid={`prod-prezzo-${p.id}`} type="number" step="0.01" value={prezzo} onChange={(e) => setPrezzo(e.target.value)} className="w-28 bg-slate-900/60 border border-slate-700 rounded-lg px-3 py-1.5 text-slate-100" /></div>
        <button data-testid={`prod-prezzo-save-${p.id}`} onClick={() => onPatch(p, { prezzo: parseFloat(prezzo) || 0 })} className="px-3 py-1.5 rounded-lg bg-cyan-400 hover:bg-cyan-300 text-slate-900 font-semibold text-sm">Salva prezzo</button>
        <label className="flex items-center gap-1.5 text-xs text-slate-300 ml-2"><input type="checkbox" checked={p.consente_nome_retro} onChange={(e) => onPatch(p, { consente_nome_retro: e.target.checked })} /> Nome retro</label>
        <label className="flex items-center gap-1.5 text-xs text-slate-300"><input type="checkbox" checked={p.includi_export_fabbrica} onChange={(e) => onPatch(p, { includi_export_fabbrica: e.target.checked })} /> Export fabbrica</label>
      </div>
      {p.richiede_taglia && (
        <div>
          <div className="flex items-center justify-between"><label className="text-[10px] uppercase text-slate-500">Taglie disponibili</label><button data-testid={`prod-taglie-save-${p.id}`} onClick={() => onPatch(p, { taglie_disponibili: taglie })} className="text-xs text-cyan-300 hover:text-cyan-200">Salva taglie</button></div>
          <SizeChips testid={`prod-taglie-${p.id}`} value={taglie} onChange={setTaglie} />
        </div>
      )}
    </div>
  );
}
